import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { AuthServiceService } from '../services/auth-service.service';
import { ActivatedRoute } from '@angular/router';
import { LangService } from '../services/lang.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  formData = {
    email: '',
    one: '',
    two: '',
    three: '',
    four: '',
    five: '',
    six: ''
  };

  isSendedCode: boolean = false;
  isSendCodeDisabled: boolean = false;
  countdown: number = 0;
  countdownInterval: any;

  notificationMessage: string = "";
  errorMessage: string = '';
  error_status: any = null;

  @ViewChild('codeOne') codeOne!: ElementRef;
  @ViewChild('codeTwo') codeTwo!: ElementRef;
  @ViewChild('codeThree') codeThree!: ElementRef;
  @ViewChild('codeFour') codeFour!: ElementRef;
  @ViewChild('codeFive') codeFive!: ElementRef;
  @ViewChild('codeSix') codeSix!: ElementRef;

  isLangModalOpen = false;

  langOptions = [
    { code: 'en', name: 'English',    flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch',    flag: '🇩🇪' },
    { code: 'ja', name: '日本語',      flag: '🇯🇵' },
    { code: 'es', name: 'Español',    flag: '🇪🇸' },
    { code: 'fr', name: 'Français',   flag: '🇫🇷' },
  ];

  constructor(
    private auth_service: AuthServiceService,
    private active_router: ActivatedRoute,
    public lang: LangService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.active_router.queryParams.subscribe(params => {
      const token = params['token'] || null;
      const chat_value = params['chat'] || null;
      if (chat_value) {
        localStorage.setItem('chat', chat_value);
      }
      if (token) {
        this.login_by_token(token);
      }
    });
  }

  getErrorMessage(key: string, params?: any): string {
    return this.translate.instant(`auth.${key}`, params || {});
  }

  login_by_token(token: string) {
    this.auth_service.login_by_token(token).subscribe({
      next: (data) => {
        localStorage.setItem('auth_client_token', data.token);
        window.location.href = '/dashboard';
      },
      error: (err) => {
        this.errorMessage = this.getErrorMessage('error');

        setTimeout(() => {
          this.errorMessage = "";
        }, 1000)
      }
    })
  }

  startCountdown(seconds: number = 100) {
    this.isSendCodeDisabled = true;
    this.countdown = seconds;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.isSendCodeDisabled = false;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  sendCodeOnEmail() {
    this.auth_service.send_login_code(this.formData.email).subscribe({
      next: (data) => {
        this.isSendedCode = true;
        this.startCountdown();
        this.notificationMessage = this.getErrorMessage('codeSent');
        this.errorMessage = "";
        this.clearCode();
      },
      error: (err) => {
        if (err.status === 400) {
          this.errorMessage = this.getErrorMessage('invalidEmail');
        } else if (err.status === 429) {
          this.errorMessage = this.getErrorMessage('error');
        } else {
          this.errorMessage = this.getErrorMessage('error');
        }
        this.notificationMessage = '';
      }
    })
  }

  isFormValid(): boolean {
    return (
      !!this.formData.email &&
      !!this.formData.one &&
      !!this.formData.two &&
      !!this.formData.three &&
      !!this.formData.four &&
      !!this.formData.five &&
      !!this.formData.six
    );
  }

  clearCode() {
    this.formData = {
      email: this.formData.email,
      one: '',
      two: '',
      three: '',
      four: '',
      five: '',
      six: ''
    }
  }

  convertEmailToLowerCase() {
    this.formData.email = this.formData.email.toLowerCase();
  }

  onCodeInput(event: Event, field: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (field === 'one' && value.length === 6 && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      this.formData.one = digits[0];
      this.formData.two = digits[1];
      this.formData.three = digits[2];
      this.formData.four = digits[3];
      this.formData.five = digits[4];
      this.formData.six = digits[5];
      this.codeSix.nativeElement.focus();
      return;
    }

    if (value.length === 1 && field !== 'six') {
      const nextFieldMap: { [key: string]: string } = {
        one: 'two',
        two: 'three',
        three: 'four',
        four: 'five',
        five: 'six'
      };
      const nextField = nextFieldMap[field];
      const nextInput = this[`code${nextField.charAt(0).toUpperCase() + nextField.slice(1)}` as keyof this] as ElementRef;
      nextInput.nativeElement.focus();
    }
  }

  onCodeKeydown(event: KeyboardEvent, field: string): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      this.errorMessage = "";
      if (!input.value && field !== 'one') {
        const prevFieldMap: { [key: string]: string } = {
          two: 'one',
          three: 'two',
          four: 'three',
          five: 'four',
          six: 'five'
        };
        const prevField = prevFieldMap[field];
        this.formData[field as keyof typeof this.formData] = '';
        const prevInput = this[`code${prevField.charAt(0).toUpperCase() + prevField.slice(1)}` as keyof this] as ElementRef;
        prevInput.nativeElement.focus();
        event.preventDefault();
      } else if (input.value) {
        this.formData[field as keyof typeof this.formData] = '';
        event.preventDefault();
      }
    }

    if (event.key === 'ArrowRight' && field !== 'six') {
      const nextFieldMap: { [key: string]: string } = {
        one: 'two',
        two: 'three',
        three: 'four',
        four: 'five',
        five: 'six'
      };
      const nextField = nextFieldMap[field];
      const nextInput = this[`code${nextField.charAt(0).toUpperCase() + nextField.slice(1)}` as keyof this] as ElementRef;
      nextInput.nativeElement.focus();
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && field !== 'one') {
      const prevFieldMap: { [key: string]: string } = {
        two: 'one',
        three: 'two',
        four: 'three',
        five: 'four',
        six: 'five'
      };
      const prevField = prevFieldMap[field];
      const prevInput = this[`code${prevField.charAt(0).toUpperCase() + prevField.slice(1)}` as keyof this] as ElementRef;
      prevInput.nativeElement.focus();
      event.preventDefault();
    }
  }

  isValidEmail() {
    return this.formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
  }

  login(): void {
    this.errorMessage = '';

    if (!this.isValidEmail()) {
      this.errorMessage = this.getErrorMessage('incorrectEmail');
      return;
    }
    const email = this.formData.email;
    const code =
      this.formData.one +
      this.formData.two +
      this.formData.three +
      this.formData.four +
      this.formData.five +
      this.formData.six;

    this.auth_service.auth_login(email, code).subscribe({
      next: (data) => {
        localStorage.setItem('auth_client_token', data.token);
        window.location.href = '/dashboard';
      },
      error: (err) => {
        if (err.status === 400) {
          this.errorMessage = this.getErrorMessage('invalidCode');
        } else if (err.status === 403) {
          this.errorMessage = this.getErrorMessage('expiredCode');
        } else {
          this.errorMessage = this.getErrorMessage('error');
        }
        this.notificationMessage = "";
      }
    });
  }
  openLangModal(): void {
    this.isLangModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeLangModal(): void {
    this.isLangModalOpen = false;
    document.body.style.overflow = '';
  }

  selectLang(code: string): void {
    this.lang.setLang(code);
    this.closeLangModal();
  }
}
